<template>
  <BaseModal
    v-model="showModal"
    title="Nouveau client"
    size="lg"
    :closable="true"
    @close="closeModal"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <div class="w-12 h-12 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center text-white shadow-lg">
          <BaseIcon name="user-plus" :size="24" />
        </div>
        <div>
          <h2 class="text-xl font-semibold text-gray-900">Nouveau client</h2>
          <p class="text-sm text-gray-500">Remplissez les informations du client</p>
        </div>
      </div>
    </template>

    <form @submit.prevent="submitForm" class="space-y-6">
      <!-- Informations générales -->
      <div>
        <div class="flex items-center gap-2 text-primary font-semibold mb-4 pb-2 border-b-2 border-primary/20">
          <BaseIcon name="info" :size="18" />
          <span>Informations générales</span>
        </div>
        <BaseInput
          v-model="form.name"
          label="Nom du client"
          placeholder="Ex: Fatou Diop"
          :error="validationErrors.name?.[0]"
          required
        />
      </div>

      <!-- Coordonnées -->
      <div>
        <div class="flex items-center gap-2 text-primary font-semibold mb-4 pb-2 border-b-2 border-primary/20">
          <BaseIcon name="phone" :size="18" />
          <span>Coordonnées</span>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <BaseInput
            v-model="form.phone"
            label="Téléphone"
            type="tel"
            placeholder="Ex: +221 77 123 45 67"
            :error="validationErrors.phone?.[0]"
          />
          <BaseInput
            v-model="form.email"
            label="Email"
            type="email"
            placeholder="Ex: jean.dupont@email.com"
            :error="validationErrors.email?.[0]"
          />
        </div>
      </div>

      <!-- Adresse -->
      <div>
        <div class="flex items-center gap-2 text-primary font-semibold mb-4 pb-2 border-b-2 border-primary/20">
          <BaseIcon name="map-pin" :size="18" />
          <span>Adresse</span>
        </div>
        <div class="space-y-4">
          <BaseInput
            v-model="form.address"
            label="Adresse"
            placeholder="Ex: 123 Rue de la République"
            :error="validationErrors.address?.[0]"
          />
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BaseInput
              v-model="form.city"
              label="Ville"
              placeholder="Ex: Dakar"
              :error="validationErrors.city?.[0]"
            />
            <BaseInput
              v-model="form.state"
              label="Région/État"
              placeholder="Ex: Dakar"
              :error="validationErrors.state?.[0]"
            />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BaseInput
              v-model="form.zip"
              label="Code postal"
              placeholder="Ex: 10200"
              :error="validationErrors.zip?.[0]"
            />
            <BaseInput
              v-model="form.country"
              label="Pays"
              placeholder="Ex: Sénégal"
              :error="validationErrors.country?.[0]"
            />
          </div>
        </div>
      </div>
    </form>

    <template #footer>
      <div class="flex items-center justify-between w-full">
        <span class="text-xs text-gray-500">* Champs obligatoires</span>
        <div class="flex gap-3">
          <BaseButton variant="ghost" @click="closeModal">
            Annuler
          </BaseButton>
          <BaseButton
            variant="primary"
            :loading="isSubmitting"
            @click="submitForm"
          >
            <template #icon-left>
              <BaseIcon name="check" :size="18" />
            </template>
            Créer le client
          </BaseButton>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { useToastStore } from '@/store/modules';
import customerService from '@/services/customerService';
import { BaseModal, BaseButton, BaseInput, BaseIcon } from '@/components/base';

export default {
  name: 'CreateCustomerModal',
  components: {
    BaseModal,
    BaseButton,
    BaseInput,
    BaseIcon
  },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'created'],
  setup() {
    const toastStore = useToastStore();
    return { toastStore };
  },
  data() {
    return {
      form: {
        name: '',
        company: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        country: ''
      },
      validationErrors: {},
      isSubmitting: false
    };
  },
  computed: {
    showModal: {
      get() {
        return this.show;
      },
      set(value) {
        if (!value) {
          this.closeModal();
        }
      }
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.resetForm();
      }
    }
  },
  methods: {
    resetForm() {
      this.form = {
        name: '',
        company: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        country: ''
      };
      this.validationErrors = {};
    },
    validateForm() {
      let isValid = true;
      this.validationErrors = {};

      if (!this.form.name || !this.form.name.trim()) {
        this.validationErrors.name = ['Le nom du client est requis'];
        isValid = false;
      } else if (this.form.name.length > 255) {
        this.validationErrors.name = ['Le nom ne doit pas dépasser 255 caractères'];
        isValid = false;
      }

      if (this.form.email && this.form.email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.form.email)) {
          this.validationErrors.email = ['Adresse email invalide'];
          isValid = false;
        }
      }

      return isValid;
    },
    async submitForm() {
      if (!this.validateForm()) {
        return;
      }

      this.isSubmitting = true;
      const formData = { ...this.form };

      Object.keys(formData).forEach(key => {
        if (!formData[key] || !formData[key].toString().trim()) {
          formData[key] = null;
        }
      });

      try {
        const response = await customerService.createCustomer(formData);
        const customer = response.data.customer || response.data;

        this.toastStore.addToast({
          type: 'success',
          message: 'Client créé avec succès!'
        });

        this.$emit('created', customer);
        this.closeModal();
      } catch (error) {
        console.error('Erreur lors de la création du client:', error);

        if (error.response && error.response.status === 422) {
          const validationErrors = error.response.data.errors || {};
          this.validationErrors = validationErrors;
        } else {
          this.toastStore.addToast({
            type: 'error',
            message: error.response?.data?.message || 'Une erreur est survenue lors de la création du client',
            duration: 5000
          });
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    closeModal() {
      this.$emit('close');
    }
  }
};
</script>
